import type { ConsolidatedDeepCheckResult } from '../../../../../services/admin/adminService';
import type { TFn } from '../types';

/**
 * Turn a deep-check result into a UI outcome. A totals reconciliation (escalation for split-storage
 * factuurs like hanos/sligro) takes priority over the raw missing-number list: a 'correct'
 * reconciliation means the factuur IS fully captured even when numbers didn't line up.
 *
 * The pakbon check is the exception — there one bon IS one row, so a bon number the document prints
 * and we never stored is a real gap regardless of what the totals say.
 */
export function deepCheckOutcome(r: ConsolidatedDeepCheckResult, t: TFn): { ok: boolean; msg: string } {
  if (r.totals) {
    const to = r.totals;
    // Reconcile EX-VAT whenever our incl-VAT sum is not a real one, or the two sides compare apples to
    // oranges. Two ways that happens:
    //  - a pakbon: dzw and bidfood delivery notes print no VAT at all;
    //  - a factuur on a platform that stores no VAT-inclusive order total (bidfood — normalizeProductData
    //    passes totalWithTaxes undefined), where `ourTotalInclVat` is just the ex-VAT sum handed back by
    //    the `totalWithTaxes ?? total` fallback. Comparing THAT against the factuur's FACTUURBEDRAG put a
    //    permanent ~10% phantom gap on every bidfood invoice, in the reconciled string as much as the
    //    mismatch one (10KX3M: "ours €11398.28 vs factuur €16040.88" — the real pair is 11398.28 vs the
    //    BTW recap's 14508.68).
    const isPakbon = to.documentType === 'pakbon';
    const exVatOnly = isPakbon || to.ourTotalInclVat === to.ourTotalExVat;
    const ours = (exVatOnly ? to.ourTotalExVat : to.ourTotalInclVat).toFixed(2);
    const docTotal = exVatOnly ? (to.invoiceTotalExVat ?? to.invoiceTotalInclVat) : to.invoiceTotalInclVat;
    const invoice = docTotal != null ? docTotal.toFixed(2) : '?';
    // The pakbon strings say "ex BTW" in their own text; the factuur ones carry it as a placeholder so
    // the basis of the comparison is never implicit.
    const vat = exVatOnly ? t('adminTesting.review.deepCheckExVatSuffix') : '';
    const key = (name: string) => `adminTesting.review.${isPakbon ? 'deepCheckPakbon' : 'deepCheckTotals'}${name}`;
    // A missing bon number is a hard fail even if the model liked the totals — it means a delivery on
    // the document never became a row, so nothing could have reconciled it.
    if (isPakbon && r.missing.length > 0) {
      return { ok: false, msg: t('adminTesting.review.deepCheckPakbonMissingBons', { missing: r.missing.length, total: r.referenced.length, list: r.missing.join(', '), reason: to.reasoning }) };
    }
    if (to.verdict === 'correct') return { ok: true, msg: t(key('Reconciled'), { count: to.orderCount, ours, invoice, vat }) };
    if (to.verdict === 'wrong') return { ok: false, msg: t(key('Mismatch'), { ours, invoice, reason: to.reasoning, vat }) };
    return { ok: false, msg: t(key('Inconclusive'), { reason: to.reasoning }) };
  }
  if (!r.ok) return { ok: false, msg: r.reason || t('adminTesting.review.deepCheckFailed') };
  // Backprocessing emails carry a per-file breakdown — report which file's orders are missing.
  if (r.files) {
    if (r.missing.length === 0) {
      return { ok: true, msg: t('adminTesting.review.deepCheckBackprocessingAllStored', { orders: r.referenced.length, files: r.files.length }) };
    }
    const perFile = r.files.filter((f) => f.missing.length > 0).map((f) => `${f.filename}: ${f.missing.join(', ')}`).join(' | ');
    return { ok: false, msg: t('adminTesting.review.deepCheckBackprocessingMissing', { missing: r.missing.length, total: r.referenced.length, list: perFile }) };
  }
  return r.missing.length === 0
    ? { ok: true, msg: t('adminTesting.review.deepCheckAllStored', { count: r.referenced.length }) }
    : { ok: false, msg: t('adminTesting.review.deepCheckMissing', { missing: r.missing.length, total: r.referenced.length, list: r.missing.join(', ') }) };
}
